import { useCallback, useState } from 'react';
import {
  readStorageValue,
  ReadStorageValueProps,
  removeStorageValue,
  saveStorageValue,
} from './use-storage.utils';

type UseStorageProps<T> = ReadStorageValueProps<T> & {
  initialize?: boolean;
};

export const useStorage = <T>({
  storageKey,
  fallbackValue,
  validate,
  shouldStoreAsJson = true,
  initialize = true,
}: UseStorageProps<T>) => {
  const [value, setValue] = useState(
    initialize
      ? readStorageValue({ storageKey, fallbackValue, validate, shouldStoreAsJson })
      : fallbackValue
  );
  const save = useCallback(
    (value: T) => {
      saveStorageValue({ storageKey, value, shouldStoreAsJson });
      setValue(value);
    },
    [storageKey, shouldStoreAsJson]
  );

  const read = useCallback(() => {
    const persistedValue = readStorageValue({
      storageKey,
      fallbackValue,
      validate,
      shouldStoreAsJson,
    });
    setValue(persistedValue);

    return persistedValue;
  }, [storageKey, fallbackValue, validate, shouldStoreAsJson]);

  const remove = useCallback(() => {
    removeStorageValue(storageKey);
    setValue(fallbackValue);
  }, [fallbackValue, storageKey]);

  return {
    value,
    save,
    read,
    remove,
  };
};
